import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MultiSelect, Popover, Select } from '@mantine/core';
import { Input } from '@shared/ui';
import { IoChevronDown } from 'react-icons/io5';
import { useAssemblyTypes, useColors, useFlowerTypes } from '@modules/products';
import { useCategories } from '@modules/categories';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import type { ProductFiltersValues } from '@shared/components/ProductFilters';

export const PRODUCT_BADGE_OPTIONS = [
  { value: 'promo', label: 'Продвижение' },
  { value: 'sale', label: 'Акция' },
  { value: 'popular', label: 'Популярное' },
  { value: 'new', label: 'Новинка' },
  { value: 'hidden', label: 'Скрыто' },
  { value: 'oos', label: 'Нет в наличии' },
] as const;

export type ProductBadgeFilter = (typeof PRODUCT_BADGE_OPTIONS)[number]['value'];

const BADGE_VALUES = new Set<string>(PRODUCT_BADGE_OPTIONS.map((option) => option.value));

const RATING_OPTIONS = [5, 4, 3, 2, 1].map((rating) => ({
  value: String(rating),
  label: String(rating),
}));

export type ProductsPageFilters = ProductFiltersValues & {
  ratings: number[];
  badges: ProductBadgeFilter[];
};

export const EMPTY_PRODUCTS_FILTERS: ProductsPageFilters = {
  colorIds: [],
  flowerTypeIds: [],
  assemblyTypeIds: [],
  minPrice: '',
  maxPrice: '',
  sellerId: '',
  categoryId: '',
  ratings: [],
  badges: [],
};

const PRODUCTS_FILTERS_STORAGE_KEY = 'admin-products-filters';

function positiveIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0);
}

function priceString(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || amount < 0) return '';
  return trimmed;
}

function idString(value: unknown): string {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) return String(value);
  if (typeof value === 'string' && /^\d+$/.test(value.trim()) && Number(value) > 0) return value.trim();
  return '';
}

function ratingList(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value.filter(
        (rating): rating is number =>
          typeof rating === 'number' && Number.isInteger(rating) && rating >= 1 && rating <= 5,
      ),
    ),
  ];
}

function badgeList(value: unknown): ProductBadgeFilter[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value.filter((badge): badge is ProductBadgeFilter => typeof badge === 'string' && BADGE_VALUES.has(badge)),
    ),
  ];
}

export function readStoredProductsFilters(): ProductsPageFilters {
  try {
    const raw = localStorage.getItem(PRODUCTS_FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_PRODUCTS_FILTERS;
    const parsed = JSON.parse(raw) as Partial<ProductsPageFilters>;
    return {
      colorIds: positiveIds(parsed.colorIds),
      flowerTypeIds: positiveIds(parsed.flowerTypeIds),
      assemblyTypeIds: positiveIds(parsed.assemblyTypeIds),
      minPrice: priceString(parsed.minPrice),
      maxPrice: priceString(parsed.maxPrice),
      sellerId: idString(parsed.sellerId),
      categoryId: idString(parsed.categoryId),
      ratings: ratingList(parsed.ratings),
      badges: badgeList(parsed.badges),
    };
  } catch {
    return EMPTY_PRODUCTS_FILTERS;
  }
}

export function writeStoredProductsFilters(filters: ProductsPageFilters) {
  localStorage.setItem(PRODUCTS_FILTERS_STORAGE_KEY, JSON.stringify(filters));
}

export function productsFiltersSelectedCount(filters: ProductsPageFilters): number {
  return (
    filters.colorIds.length +
    filters.flowerTypeIds.length +
    filters.assemblyTypeIds.length +
    filters.ratings.length +
    filters.badges.length +
    (filters.minPrice.trim() ? 1 : 0) +
    (filters.maxPrice.trim() ? 1 : 0) +
    (filters.sellerId?.trim() ? 1 : 0) +
    (filters.categoryId?.trim() ? 1 : 0)
  );
}

function toIdList(values: string[]): number[] {
  return values.map(Number).filter((id) => Number.isInteger(id) && id > 0);
}

type ProductsFiltersPopoverProps = {
  value: ProductsPageFilters;
  selectedCount: number;
  canPickSeller: boolean;
  onChange: (filters: ProductsPageFilters) => void;
};

export default function ProductsFiltersPopover({
  value,
  selectedCount,
  canPickSeller,
  onChange,
}: ProductsFiltersPopoverProps) {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          '.products-page__filters-trigger, .products-filters-popover, .mantine-Popover-dropdown, .mantine-Combobox-dropdown',
        )
      ) {
        return;
      }
      setOpened(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [opened]);

  const sellersQuery = useQuery({
    queryKey: ['sellers', 'list'],
    queryFn: () => sellersApi.getAll(),
    enabled: opened && canPickSeller,
  });
  const { data: categories = [] } = useCategories();
  const { data: colors = [] } = useColors();
  const { data: flowerTypes = [] } = useFlowerTypes();
  const { data: assemblyTypes = [] } = useAssemblyTypes();

  const shops = useMemo(() => {
    return (sellersQuery.data ?? [])
      .slice()
      .sort((a, b) => a.shopName.localeCompare(b.shopName, 'ru'));
  }, [sellersQuery.data]);

  const categoryOptions = useMemo(
    () =>
      categories
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
        .map((category) => ({ value: String(category.id), label: category.name })),
    [categories],
  );

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      offset={8}
      withinPortal
      closeOnClickOutside={false}
      trapFocus={false}
      classNames={{ dropdown: 'products-filters-popover' }}
    >
      <Popover.Target>
        <button
          type="button"
          className={`products-page__filters-trigger${selectedCount > 0 ? ' products-page__filters-trigger--active' : ''}${opened ? ' products-page__filters-trigger--open' : ''}`}
          aria-label="Фильтры товаров"
          aria-expanded={opened}
          onClick={() => setOpened((current) => !current)}
        >
          <span className="products-page__filters-trigger-label">Фильтры</span>
          {selectedCount > 0 ? (
            <span className="products-page__filters-badge">{selectedCount}</span>
          ) : null}
          <IoChevronDown size={14} aria-hidden />
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <div className="products-filters">
          <div className="products-filters__header">
            <button
              type="button"
              className="products-filters__reset"
              disabled={selectedCount === 0}
              onClick={() => onChange(EMPTY_PRODUCTS_FILTERS)}
            >
              Сбросить все
            </button>
          </div>
          {canPickSeller ? (
            <Select
              className="select"
              label="Магазин"
              size="xs"
              placeholder="Все магазины"
              searchable
              clearable
              data={shops.map((seller) => ({ value: String(seller.id), label: seller.shopName }))}
              value={value.sellerId || null}
              onChange={(next) => onChange({ ...value, sellerId: next ?? '' })}
              nothingFoundMessage="Магазины не найдены"
              comboboxProps={{ zIndex: 1100 }}
            />
          ) : null}
          <Select
            className="select"
            label="Категория"
            size="xs"
            placeholder="Все категории"
            searchable
            clearable
            data={categoryOptions}
            value={value.categoryId || null}
            onChange={(next) => onChange({ ...value, categoryId: next ?? '' })}
            nothingFoundMessage="Категории не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Цвет"
            size="xs"
            placeholder={value.colorIds.length > 0 ? undefined : 'Все'}
            searchable
            clearable
            data={colors.map((color) => ({ value: String(color.id), label: color.name }))}
            value={value.colorIds.map(String)}
            onChange={(values) => onChange({ ...value, colorIds: toIdList(values) })}
            nothingFoundMessage="Цвета не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Тип цветка"
            size="xs"
            placeholder={value.flowerTypeIds.length > 0 ? undefined : 'Все'}
            searchable
            clearable
            data={flowerTypes.map((type) => ({ value: String(type.id), label: type.name }))}
            value={value.flowerTypeIds.map(String)}
            onChange={(values) => onChange({ ...value, flowerTypeIds: toIdList(values) })}
            nothingFoundMessage="Типы не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Тип сборки"
            size="xs"
            placeholder={value.assemblyTypeIds.length > 0 ? undefined : 'Все'}
            searchable
            clearable
            data={assemblyTypes.map((type) => ({ value: String(type.id), label: type.name }))}
            value={value.assemblyTypeIds.map(String)}
            onChange={(values) => onChange({ ...value, assemblyTypeIds: toIdList(values) })}
            nothingFoundMessage="Типы не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Рейтинг"
            size="xs"
            placeholder={value.ratings.length > 0 ? undefined : 'Все'}
            clearable
            data={RATING_OPTIONS}
            value={value.ratings.map(String)}
            onChange={(values) =>
              onChange({
                ...value,
                ratings: values
                  .map(Number)
                  .filter((rating) => Number.isInteger(rating) && rating >= 1 && rating <= 5),
              })
            }
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect products-filters__span"
            label="Бейджи"
            size="xs"
            placeholder={value.badges.length > 0 ? undefined : 'Все'}
            clearable
            data={[...PRODUCT_BADGE_OPTIONS]}
            value={value.badges}
            onChange={(values) =>
              onChange({
                ...value,
                badges: values.filter((badge): badge is ProductBadgeFilter => BADGE_VALUES.has(badge)),
              })
            }
            comboboxProps={{ zIndex: 1100 }}
          />
          <div className="products-filters__prices">
            <Input
              label="Мин. цена"
              size="xs"
              type="number"
              min={0}
              step={0.01}
              placeholder="От"
              value={value.minPrice}
              onChange={(event) => onChange({ ...value, minPrice: event.currentTarget.value })}
            />
            <Input
              label="Макс. цена"
              size="xs"
              type="number"
              min={0}
              step={0.01}
              placeholder="До"
              value={value.maxPrice}
              onChange={(event) => onChange({ ...value, maxPrice: event.currentTarget.value })}
            />
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
